/**
 * @file Print Packages related fragments.
 */

import gql from 'graphql-tag';

import {
  printProductSizeServiceDetails,
  printProductSizeBaseDetails,
  printProductSizeRoesGraphicNodeDetails,
} from './print-product';
import { printPartnerOrderServiceDetails } from './print-partner';
import { VendorDetails } from './account';
import { DigitalBundleDetails } from './digital-bundles';

export const PrintPackagePricingDetails = gql`
  fragment PrintPackagePricingDetails on PrintPackagePricing {
    id
    days_offset_type
    days_offset_from
    days_offset_to
    price {
      value
      currency
    }
  }
`;

export const PrintPackageDetails = gql`
  fragment PrintPackageDetails on PrintPackage {
    id
    name
    internal_name
    price {
      value
      currency
    }
    max_checkout_items
    preview_asset_image {
      id
      url
    }
    account {
      ...VendorDetails
    }
    items(slice: { limit: 9999 }) {
      total
      records {
        id
        type {
          id
          name
          active
          min_price {
            value
            currency
          }
        }
        custom_print {
          id
          name
          type
          subtype
          preview_image {
            id
            url
          }
          photos_no
          background_image {
            id
            url
          }
          account {
            id
            logo_image {
              id
              url
            }
          }
          price {
            value
            currency
          }
          custom_print_caption_enabled
          shipping_required
          shipping_enabled
          card {
            id
            card_side_groups {
              records {
                id
                card_sides {
                  records {
                    id
                    card_template {
                      id
                      print_size {
                        id
                        name
                      }
                      print_product {
                        id
                        name
                        print_partner {
                          id
                          name
                        }
                        sizes(slice: { limit: 999 }) {
                          records {
                            id
                            sku
                            print_size {
                              id
                            }
                            price {
                              value
                              currency
                            }
                            min_price {
                              value
                              currency
                            }
                            services {
                              total
                              records {
                                ...printProductSizeServiceDetails
                              }
                            }
                            roes_info {
                              layout_file
                              graphic_nodes(slice: { limit: 99 }) {
                                total
                                records {
                                  ...printProductSizeRoesGraphicNodeDetails
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        print_product_configuration {
          print_product {
            id
            name
            is_standard
            card {
              id
            }
          }
          print_product_size {
            id
            print_size {
              id
              name
            }
            price {
              value
              currency
            }
            min_price {
              value
              currency
            }
            services {
              total
              records {
                ...printProductSizeServiceDetails
              }
            }
            roes_info {
              layout_file
              graphic_nodes(slice: { limit: 99 }) {
                total
                records {
                  ...printProductSizeRoesGraphicNodeDetails
                }
              }
            }
          }
          print_product_size_services {
            total
            records {
              id
              print_product_size_service {
                ...printProductSizeServiceDetails
              }
            }
          }
          print_product_size_roes_graphic_node {
            ...printProductSizeRoesGraphicNodeDetails
          }
        }
        tag_template {
          __typename
          ... on PictureDayTagTemplate {
            id
            name
          }
        }
        quantity
        is_optional
        album_folder_print_package_items(slice: { limit: 1 }) {
          total
          records {
            id
          }
        }
        digital_bundle {
          ...DigitalBundleDetails
        }
        grants_purchased_identity
      }
    }
    print_partner {
      id
      name
    }
    digital_downloads_mode
    digital_downloads_upsell_price {
      value
      currency
    }
    print_order_configuration {
      print_partner_order_services {
        total
        records {
          id
          print_partner_order_service {
            ...printPartnerOrderServiceDetails
          }
        }
      }
      total_price {
        value
        currency
      }
    }
    pricing_category {
      id
      name
    }
    pricing_categories {
      total
      records {
        id
        pricing_category {
          id
          name
        }
      }
    }
    print_assets(slice: { limit: 999 }) {
      total
      records {
        id
        print_asset {
          id
          name
          type
          jersey_colors(slice: { limit: 999 }) {
            records {
              id
              jersey_color
            }
          }
          asset_image {
            id
            url
            width
            height
          }
        }
      }
    }
    jersey_colors(slice: { limit: 999 }) {
      total
      records {
        id
        jersey_color
      }
    }
    pricings(slice: { limit: 999 }) {
      total
      records {
        ...PrintPackagePricingDetails
      }
    }
    has_pending_purchased_print_package
    soft_deleted
    soft_deleted_at
    created_at
  }
  ${printProductSizeServiceDetails}
  ${printPartnerOrderServiceDetails}
  ${printProductSizeRoesGraphicNodeDetails}
  ${VendorDetails}
  ${PrintPackagePricingDetails}
  ${DigitalBundleDetails}
`;

export const AlbumPrintPackageDetails = gql`
  fragment AlbumPrintPackageDetails on PrintPackage {
    id
    album_print_packages {
      total
      records {
        id
        album {
          id
          name
        }
      }
    }
  }
`;

export const PrintPackageAlbumFolderDetails = gql`
  fragment PrintPackageAlbumFolderDetails on PrintPackage {
    id
    album_folders {
      total
      records {
        id
        name
      }
    }
  }
`;

export const PrintPackageForPurchaseDetails = gql`
  fragment PrintPackageForPurchaseDetails on PrintPackage {
    id
    name
    max_checkout_items
    preview_asset_image {
      id
      url
    }
    price {
      value
      currency
    }
    discount_price {
      value
      currency
    }
    digital_downloads_mode
    digital_downloads_upsell_price {
      value
      currency
    }
    print_package_bundle_discount_price {
      value
      currency
    }
    print_assets(slice: { limit: 999 }) {
      total
      records {
        id
        print_asset {
          id
          name
          type
          jersey_colors(slice: { limit: 999 }) {
            records {
              id
              jersey_color
            }
          }
          asset_image {
            id
            url
            width
            height
          }
        }
      }
    }
    jersey_colors(slice: { limit: 999 }) {
      total
      records {
        id
        jersey_color
      }
    }
    account {
      ...VendorDetails
    }
    print_order_configuration {
      print_partner_order_services {
        total
        records {
          id
          print_partner_order_service {
            ...printPartnerOrderServiceDetails
          }
        }
      }
      total_price {
        value
        currency
      }
    }
    items(slice: { limit: 99 }) {
      total
      records {
        id
        quantity
        type {
          id
          name
        }
        album_folder_tag {
          ... on CustomAlbumFolderTag {
            id
            name
          }
        }
        price {
          value
          currency
        }
        discount_price {
          value
          currency
        }
        is_optional
        custom_print {
          id
          name
          type
          subtype
          preview_image {
            id
            url
          }
          photos_no
          background_image {
            id
            url
          }
          account {
            id
            logo_image {
              id
              url
            }
          }
          price {
            value
            currency
          }
          custom_print_caption_enabled
          shipping_required
          shipping_enabled
          card {
            id
            card_side_groups {
              records {
                id
                side
                card_sides {
                  records {
                    id
                    card_template {
                      id
                      orientation
                      print_size {
                        id
                        name
                        width
                        height
                      }
                      print_product {
                        id
                        print_size {
                          id
                          name
                        }
                        print_partner {
                          id
                          name
                        }
                        sizes(slice: { limit: 999 }) {
                          records {
                            id
                            sku
                            print_size {
                              id
                              name
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        print_product_configuration {
          print_product {
            id
            name
            is_standard
            card {
              id
            }
            preview_asset_image {
              id
              url
            }
          }
          print_product_size {
            ...printProductSizeBaseDetails
          }
          print_product_size_services {
            total
            records {
              id
              print_product_size_service {
                ...printProductSizeServiceDetails
              }
            }
          }
          print_product_size_roes_graphic_node {
            ...printProductSizeRoesGraphicNodeDetails
          }
        }
        album_folder_print_package_items(slice: { limit: 99 }) {
          total
          records {
            id
            album_folder_print_package {
              album_folder {
                id
              }
            }
            price {
              value
              currency
            }
            discount_price {
              value
              currency
            }
            album_folder_tag {
              ... on CustomAlbumFolderTag {
                id
                name
              }
            }
          }
        }
        digital_bundle {
          ...DigitalBundleDetails
        }
        grants_purchased_identity
      }
    }
  }
  ${printProductSizeBaseDetails}
  ${printProductSizeServiceDetails}
  ${printPartnerOrderServiceDetails}
  ${printProductSizeRoesGraphicNodeDetails}
  ${VendorDetails}
  ${DigitalBundleDetails}
`;
